// ** React Imports
import {
	forwardRef,
	useMemo,
	useCallback,
	useRef,
	useLayoutEffect,
	useEffect,
	useState,
	useContext,
	Fragment,
	memo,
} from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

// ** Custom Components
import Message from "./components/Message";
import AttachmentModal from "./components/AttachmentModal";
import NextDay from "./components/NextDay";
import ReadedStatusIcon from "components/icons/ReadedStatusIcon";
import UnreadedStatusIcon from "components/icons/UnreadedStatusIcon";
import Loader from "components/layout/Loader";

// ** Custom Hooks
import useOpenClose from "hooks/useOpenClose";
import useMobileDetect from "hooks/useMobileDetectHook";

// ** Utils & Helpers
import { getConversation } from "../../helpers/messagesApiActions";
import { throttle } from "throttle-debounce";
import api from "helpers/ApiClient";

// ** Contexts
import { ConversationContext } from "../../helpers/ConversationContext";

// ** Styled Components
import { StyledWrapper } from "./styles";

// #####################################################

const ConversationChat = forwardRef(
	(
		{
			conversation: { messages, pagination, user, loading, forceRefresh },
			setConversation,
			conversationId,
			onAttachmentRemove,
			verticalScrollbarWidth,
			isVerticalScroll,
			onAcceptVideoChat,
		},
		ref
	) => {
		const history = useHistory();
		const { pathname } = useLocation();
		const { isOpen, close, open } = useOpenClose();
		const mobileDetect = useMobileDetect();
		const isIos = mobileDetect.isIos();
		const conversationContext = useContext(ConversationContext);
		const language = useSelector((state) => state.globalCommon.language);

		const previousScrollPosition = useRef(null);
		const previousMessagesLength = useRef(null);
		const prevPage = useRef(null);

		const [isScrollDispatch, setIsScrollDispatch] = useState(false);

		const triggerRef = useRef();
		const trigger = useCallback(
			(node) => {
				if (loading || !("IntersectionObserver" in window)) return;
				if (triggerRef.current) triggerRef.current.disconnect();
				triggerRef.current = new IntersectionObserver((entries) => {
					if (
						entries[0].isIntersecting &&
						prevPage.current != pagination[2] &&
						pagination[2]
					) {
						setIsScrollDispatch(true);
						previousMessagesLength.current = messages.length;
						prevPage.current = pagination[2];
						getConversation({
							page: pagination[2],
							setConversation,
							conversationId,
						});
					}
				});
				if (node) triggerRef.current.observe(node);
			},
			[messages.length, loading]
		);

		const emergencyTriggerRef = useRef();
		const emergencyTrigger = useCallback(
			(node) => {
				if (loading || !("IntersectionObserver" in window)) return;
				if (emergencyTriggerRef.current)
					emergencyTriggerRef.current.disconnect();
				emergencyTriggerRef.current = new IntersectionObserver(
					(entries) => {
						if (
							entries[0].isIntersecting &&
							prevPage.current != pagination[2] &&
							pagination[2]
						) {
							setIsScrollDispatch(true);
							previousMessagesLength.current = messages.length;
							prevPage.current = pagination[2];
							getConversation({
								page: pagination[2],
								setConversation,
								conversationId,
							});
						}
					}
				);
				if (node) emergencyTriggerRef.current.observe(node);
			},
			[messages.length, loading]
		);

		const handleAttachmentClick = useCallback(
			(event, data) => {
				history.push({
					pathname: pathname,
					hash: `attachment/${data.file_id}`,
					state: {
						data,
					},
				});
			},
			[pathname]
		);

		let IdLastVideoChatInfoInLeftSide = null;
		let IdLastVideoChatInfoInRightSide = null;

		const renderChatMessages = useMemo(() => {
			if (messages.length === 0) {
				return null;
			}

			let IdLastReadedMessageInRightSide = null;

			for (let i = messages.length - 1; i >= 0; i--) {
				const message = messages[i];
				if (
					message.read === true &&
					message.send === true &&
					(!messages[i + 1] ||
						(messages[i + 1]?.read === false &&
							messages[i + 1]?.send === true))
				) {
					IdLastReadedMessageInRightSide = message.id;
					break;
				}
			}
			for (let i = messages.length - 1; i >= 0; i--) {
				const message = messages[i];
				if (message.additional_status === 1 && message.send === false) {
					IdLastVideoChatInfoInLeftSide = message.id;
					break;
				}
			}

			for (let i = messages.length - 1; i >= 0; i--) {
				const message = messages[i];
				if (message.additional_status === 1 && message.send === true) {
					IdLastVideoChatInfoInRightSide = message.id;
					break;
				}
			}

			return messages.map((item, index) => {
				const isFirst = messages[0]?.id === item.id && !pagination[2];
				const isLast = messages[messages.length - 1].id === item.id;
				const isRightSide = item.send === true;
				// Sparates the sender's and recipient's messages.
				const isSeparator = messages[index - 1]?.send !== item.send;
				const thisMessageDate = new Date(item.date);
				const nextMessageDate = messages[index + 1]?.date
					? new Date(messages[index + 1].date)
					: null;

				let nextDay = false;
				let nextMinute = false;

				if (
					(nextMessageDate &&
						thisMessageDate.getMinutes() !==
							nextMessageDate.getMinutes()) ||
					(messages[index + 1] &&
						messages[index + 1]?.send !== item.send)
				) {
					nextMinute = true;
				} else if (nextMessageDate === null) {
					nextMinute = true;
				}

				if (
					nextMessageDate &&
					(thisMessageDate.getFullYear() !==
						nextMessageDate.getFullYear() ||
						thisMessageDate.getMonth() !==
							nextMessageDate.getMonth() ||
						thisMessageDate.getDate() !== nextMessageDate.getDate())
				) {
					nextDay = true;
				}

				return (
					<Fragment key={`${item.id}-${index}`}>
						{isFirst && item.date && <NextDay date={item.date} />}
						<Message
							item={item}
							onAttachmentClick={handleAttachmentClick}
							onAttachmentRemove={onAttachmentRemove}
							chatPartner={user}
							isLast={isLast}
							isRightSide={isRightSide}
							isSeparator={isSeparator}
							IdLastReadedMessageInRightSide={
								IdLastReadedMessageInRightSide
							}
							IdLastVideoChatInfoInLeftSide={
								IdLastVideoChatInfoInLeftSide
							}
							IdLastVideoChatInfoInRightSide={
								IdLastVideoChatInfoInRightSide
							}
							conversationUserAvatar={user.avatar}
							nextMinute={nextMinute}
							onAcceptVideoChat={onAcceptVideoChat}
							trigger={
								isIos
									? null
									: messages[
											pagination[1] === 1
												? 3
												: Math.round(
														(messages.length -
															previousMessagesLength.current) /
															3
												  )
									  ]?.id === item.id &&
									  messages[0].id !== item.id
									? trigger
									: null
							}
						/>
						{nextDay && messages[index + 1]?.date && (
							<NextDay date={messages[index + 1].date} />
						)}
					</Fragment>
				);
			});
		}, [messages.length, forceRefresh]);

		const isFirstRender = useRef(true);
		useEffect(() => {
			if (conversationId && isFirstRender.current) {
				isFirstRender.current = false;
				previousMessagesLength.current = messages.length;
				getConversation({
					page: 1,
					setConversation,
					conversationId,
				});
			} else if (conversationId) {
				api.get(`/message`, {
					params: {
						id: conversationId,
						page: 1,
						reverse: true,
					},
					headers: {
						Accept: "application/api.1.1+json",
					},
				}).then(({ data } = {}) => {
					if (!data) return;
					const { user } = data;
					setConversation((prev) => {
						return {
							...prev,
							user: {
								...prev.user,
								editor: {
									disabled: user?.editor?.disabled,
									info: user?.editor?.info,
								},
							},
						};
					});
				});
			}
		}, [language]);

		useEffect(() => {
			if (!isScrollDispatch) {
				setTimeout(() => {
					if (ref.current) {
						ref.current.scrollTop = ref.current.scrollHeight;
					}
				}, 100);
			}
		}, [messages?.length]);

		useEffect(() => {
			if (conversationContext.showAcceptVideoChatButton) {
				setTimeout(() => {
					if (ref.current) {
						ref.current.scrollTop = ref.current.scrollHeight;
					}
				}, 100);
			}
		}, [conversationContext.showAcceptVideoChatButton]);

		/* MARCIN_P: Saving scroll position after loading new messages with scroll up */
		useMemo(() => {
			previousScrollPosition.current =
				ref?.current?.scrollHeight - ref?.current?.scrollTop;
		}, [messages?.length]);

		useLayoutEffect(() => {
			if (ref?.current) {
				ref.current.scrollTop =
					ref.current.scrollHeight - previousScrollPosition.current;
			}
			setIsScrollDispatch(false);
		}, [messages?.length]);

		useEffect(() => {
			if (!isIos) return;
			const checkScroll = ({ srcElement }) => {
				if (
					!loading &&
					pagination[2] &&
					prevPage.current != pagination[2] &&
					srcElement?.scrollTop === 0
				) {
					prevPage.current = pagination[2];
					setIsScrollDispatch(true);
					getConversation({
						page: pagination[2],
						setConversation,
						conversationId,
					});
				}
			};

			const throttleFunc = throttle(300, checkScroll);
			if (ref.current)
				ref.current.addEventListener("scroll", throttleFunc);

			return () => {
				if (ref.current) {
					ref.current.removeEventListener("scroll", throttleFunc);
				}
			};
		}, [loading]);

		useEffect(() => {
			if (ref.current === null || !isIos) return;
			const hasVerticalScrollbar =
				ref.current.scrollHeight > ref.current.clientHeight;

			if (!hasVerticalScrollbar && pagination[2]) {
				getConversation({
					page: pagination[2],
					setConversation,
					conversationId,
				});
			}
		}, [pagination[2]]);

		// #####################################################

		return (
			<>
				<AttachmentModal isOpen={isOpen} close={close} open={open} />
				<StyledWrapper
					ref={ref}
					isEmpty={messages.length === 0}
					verticalScrollbarWidth={verticalScrollbarWidth}
					isVerticalScroll={isVerticalScroll}
				>
					{loading && <Loader centerOnlyIcon />}
					{!loading &&
						pagination[2] &&
						!isIos &&
						!(pagination[1] > 0 && messages.length === 0) && (
							<div
								ref={emergencyTrigger}
								style={{
									height: "1px",
									pointerEvents: "none",
								}}
							/>
						)}
					{renderChatMessages}
					{messages.length > 0 &&
						messages[messages.length - 1].send &&
						!messages[messages.length - 1].read &&
						!messages[messages.length - 1].sending && (
							<div style={{ marginLeft: "auto" }}>
								<UnreadedStatusIcon customMargin="0.25rem 0 1px 0" />
							</div>
						)}
					{messages.length > 0 &&
						messages[messages.length - 1].send &&
						messages[messages.length - 1].read && (
							<div style={{ marginLeft: "auto" }}>
								<ReadedStatusIcon
									srcAvatar={user.avatar}
									customMargin={"0.25rem 0 1px 0"}
								/>
							</div>
						)}
				</StyledWrapper>
			</>
		);
	}
);

// #####################################################
const areEqual = (prevProps, nextProps) => {
	return (
		prevProps.conversationId === nextProps.conversationId &&
		prevProps.onAcceptVideoChat === nextProps.onAcceptVideoChat &&
		prevProps.verticalScrollbarWidth === nextProps.verticalScrollbarWidth &&
		prevProps.isVerticalScroll === nextProps.isVerticalScroll &&
		prevProps.conversation.messages.length ===
			nextProps.conversation.messages.length &&
		prevProps.conversation.forceRefresh ===
			nextProps.conversation.forceRefresh &&
		prevProps.conversation.loading === nextProps.conversation.loading
	);
};

export default memo(ConversationChat, areEqual);

// #####################################################
